<i18n>
{
  "de": {
    "filters": "Filter",
    "filtersActive": "{count} Filter aktiv | {count} Filter aktiv",
    "resetAll": "Alle zurücksetzen",
    "close": "Schließen"
  },
  "en": {
    "filters": "Filters",
    "filtersActive": "{count} filter active | {count} filters active",
    "resetAll": "Reset All",
    "close": "Close"
  }
}
</i18n>

<template>
  <div>
    <!-- Floating Action Button -->
    <transition name="fab-scale">
      <button
        v-if="showButton"
        class="floating-filter-btn"
        :class="{
          'active': hasActiveFilters,
          'pulsing': filtersPulse,
          'floating-filter-btn--elevated': isElevated,
          'floating-filter-btn--close-mode': bottomSheetOpen
        }"
        @click="toggleBottomSheet"
      >
        <i
          v-if="!bottomSheetOpen"
          class="icon-filter"
        />
        <i
          v-else
          class="icon-times"
        />
        <span
          v-if="activeFilterCount > 0 && !bottomSheetOpen && isPostsRoute"
          class="filter-badge"
        >{{ activeFilterCount }}</span>
        <span
          v-if="hasTopicFilters && !activeFilterCount && !bottomSheetOpen"
          class="topic-dot"
        />
      </button>
    </transition>

    <!-- Bottom Sheet Modal -->
    <transition name="bottom-sheet">
      <div
        v-if="bottomSheetOpen"
        class="bottom-sheet-backdrop floating-filter-bottom-sheet"
        :style="userColorStyles"
        @click="closeBottomSheet"
      >
        <div
          class="bottom-sheet"
          @click.stop
        >
          <!-- Header -->
          <div class="bottom-sheet-header">
            <h3>{{ $t('filters') }}</h3>
          </div>

          <!-- Filter Content -->
          <div
            class="bottom-sheet-content"
            @click="handleContentClick"
          >
            <!-- Posts Filter Content -->
            <filter-posts-content
              v-if="isPostsRoute"
              ref="filterContent"
              :layout="'mobileBottomSheet'"
              :in-bottom-sheet="true"
              @filtersChanged="onFiltersChanged"
            />
            <!-- Shop Filter Content -->
            <filter-shop-content
              v-else-if="isShopRoute"
              ref="shopFilterContent"
              :layout="'mobileBottomSheet'"
              :in-bottom-sheet="true"
              @filtersChanged="onFiltersChanged"
            />
          </div>

          <!-- Footer Actions -->
          <div
            class="bottom-sheet-footer"
          >
            <button
              v-if="hasActiveFilters"
              class="reset-btn"
              @click="resetAllFilters"
            >
              {{ $t('resetAll') }}
            </button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import FilterPostsContent from '~/components/filter-posts-content'
import FilterShopContent from '~/components/filter-shop-content'
import profileColorsMixin from '~/mixins/profileColors'

export default {
  name: 'FloatingFilterButton',
  components: {
    FilterPostsContent,
    FilterShopContent
  },
  mixins: [profileColorsMixin],
  data() {
    return {
      bottomSheetOpen: false,
      filtersPulse: false,
      pulseTimeout: null,
      isElevated: false,
      scrollTimeout: null,
      lastScrollY: 0,
      footerToggleObserver: null
    }
  },
  computed: {
    isPostsRoute() {
      return this.$route && this.$route.name &&
             (this.$route.name.startsWith('id-posts') || this.$route.name.startsWith('id___'))
    },
    isShopRoute() {
      return this.$route && this.$route.name &&
             this.$route.name.includes('shop')
    },
    showButton() {
      // Show on posts and shop routes on mobile
      return this.isMobileView && (this.isPostsRoute || this.isShopRoute)
    },
    hasActiveFilters() {
      if (this.isShopRoute) {
        return this.hasActiveShopFilters
      }
      return this.activeFilterCount > 0 || this.hasTopicFilters
    },
    // Posts filter count
    activeFilterCount() {
      if (!this.postFilter) return 0
      return this.postFilter.product.length +
        this.postFilter.reward.length +
        this.postFilter.months.length +
        this.postFilter.contents.length +
        (this.postFilter.public ? 1 : 0) +
        (this.postFilter.limitToFollowers ? 1 : 0)
    },
    hasTopicFilters() {
      return this.postFilter && this.postFilter.tags && this.postFilter.tags.length > 0
    },
    // Shop filter active check
    hasActiveShopFilters() {
      if (!this.shopFilter) return false
      return this.shopFilter.type !== null ||
             this.shopFilter.sortBy !== 'date' ||
             this.shopFilter.sortOrder !== 'desc'
    },
    ...mapState(['postFilter', 'shopFilter'])
  },
  watch: {
    hasActiveFilters(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.triggerPulse()
      }
    }
  },
  methods: {
    toggleBottomSheet() {
      if (this.bottomSheetOpen) {
        this.closeBottomSheet()
      } else {
        this.openBottomSheet()
      }
    },
    openBottomSheet() {
      this.bottomSheetOpen = true
      document.body.classList.add('no-scroll')
    },
    closeBottomSheet() {
      this.bottomSheetOpen = false
      document.body.classList.remove('no-scroll')
    },
    resetAllFilters() {
      if (this.isShopRoute) {
        this.$root.$emit('resetAllShopFilters')
        this.resetShopFilter()
      } else {
        this.$root.$emit('resetAllFilters')
      }
      this.closeBottomSheet()
      this.triggerPulse()
    },
    ...mapActions(['resetShopFilter']),
    onFiltersChanged() {
      this.triggerPulse()
    },
    handleContentClick(event) {
      // Don't close if clicking directly on multi-select components
      if (event.target.closest('.multi-select')) {
        return
      }

      // Find all open multi-select dropdowns and close them by dispatching a click outside event
      const multiSelects = this.$el.querySelectorAll('.multi-select')
      multiSelects.forEach(multiSelect => {
        const checkboxLayer = multiSelect.querySelector('.checkboxLayer')

        // Check if dropdown is visible
        if (checkboxLayer && checkboxLayer.offsetHeight > 0 && checkboxLayer.offsetWidth > 0) {
          // Dispatch a click event on the document to trigger the multi-select's own click-outside handling
          const clickOutsideEvent = new MouseEvent('click', {
            bubbles: true,
            cancelable: true,
            view: window
          })

          // Dispatch the event on the document, which should trigger the multi-select's click-outside handler
          document.dispatchEvent(clickOutsideEvent)
        }
      })
    },
    triggerPulse() {
      this.filtersPulse = true
      clearTimeout(this.pulseTimeout)
      this.pulseTimeout = setTimeout(() => {
        this.filtersPulse = false
      }, 300)
    },
    handleScroll() {
      // Throttle scroll events for performance
      if (this.scrollTimeout) return

      this.scrollTimeout = setTimeout(() => {
        this.checkFooterProximity()
        this.scrollTimeout = null
      }, 16) // ~60fps
    },
    checkFooterProximity() {
      // Check for footer-toggle (only if it's visible, not hidden when footer is expanded)
      const footerToggle = document.querySelector('.footer-toggle')
      if (!footerToggle || footerToggle.classList.contains('footer-toggle--hidden')) {
        this.isElevated = false
        return
      }

      const footerRect = footerToggle.getBoundingClientRect()
      const windowHeight = window.innerHeight
      const currentScrollY = window.scrollY

      // Determine scroll direction
      const isScrollingDown = currentScrollY > this.lastScrollY
      const isScrollingUp = currentScrollY < this.lastScrollY

      // Different thresholds based on scroll direction for hysteresis effect
      let threshold
      if (isScrollingDown) {
        // When scrolling down, wait longer before elevating (closer to footer)
        threshold = 50
      } else if (isScrollingUp) {
        // When scrolling up, return to bottom position earlier (smaller threshold)
        threshold = 10
      } else {
        // No scroll change, use current state threshold
        threshold = this.isElevated ? 50 : 150
      }

      const shouldElevate = footerRect.top < windowHeight + threshold

      this.isElevated = shouldElevate
      this.lastScrollY = currentScrollY
    }
  },
  mounted() {
    // Set up scroll listener for footer proximity detection
    if (typeof window !== 'undefined') {
      window.addEventListener('scroll', this.handleScroll, { passive: true })
      // Initial check
      this.checkFooterProximity()

      // Set up MutationObserver to watch for footer toggle visibility changes
      this.$nextTick(() => {
        const footerToggle = document.querySelector('.footer-toggle')
        if (footerToggle) {
          this.footerToggleObserver = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
              if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
                this.checkFooterProximity()
              }
            })
          })
          this.footerToggleObserver.observe(footerToggle, { attributes: true })
        }
      })
    }
  },
  beforeDestroy() {
    document.body.classList.remove('no-scroll')
    clearTimeout(this.pulseTimeout)
    clearTimeout(this.scrollTimeout)
    if (typeof window !== 'undefined') {
      window.removeEventListener('scroll', this.handleScroll)
    }
    if (this.footerToggleObserver) {
      this.footerToggleObserver.disconnect()
    }
  }
}
</script>

<style lang="scss" scoped>
@import 'assets/sass/_variables';

.floating-filter-btn {
  position: fixed;
  bottom: 10px;
  right: 10px;
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: #f5f5f5;
  border: 2px solid #e0e0e0;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  z-index: 20;
  color: #666;

  // Elevated position when footer toggle is nearby
  &--elevated {
    bottom: 60px; // Adjusted for new bar-style footer toggle
  }

  // Close mode styling when bottom sheet is open
  &--close-mode {
    position: fixed;
    bottom: 12px;
    background: #666;
    border-color: #666;
    color: white;

    &:hover {
      background: #555;
      border-color: #555;
    }
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
  }

  &.active {
    background: var(--primary-color, #fb3e52);
    border-color: var(--primary-color, #fb3e52);
    color: white;

    &:hover {
      background: var(--primary-color-hover, #e63946);
    }
  }

  &.pulsing {
    animation: pulse 0.3s ease;
  }

  .icon-filter,
  .icon-times {
    width: 48px;
    height: 48px;
  }

  .filter-badge {
    position: absolute;
    top: -6px;
    right: -6px;
    background: #ff4757;
    color: white;
    border-radius: 10px;
    padding: 2px 6px;
    font-size: 12px;
    font-weight: 600;
    min-width: 20px;
    text-align: center;
  }

  .topic-dot {
    position: absolute;
    top: 8px;
    right: 8px;
    width: 8px;
    height: 8px;
    background: #ff4757;
    border-radius: 50%;
  }
}

.bottom-sheet-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 19;
  display: flex;
  align-items: flex-end;
}

.bottom-sheet {
  width: 100%;
  max-height: 85vh;
  background: white;
  border-radius: 20px 20px 0 0;
  display: flex;
  flex-direction: column;
}

.bottom-sheet-header {
  padding: 20px 20px 15px 20px;
  border-bottom: 1px solid #e0e0e0;
  background: white;
  border-radius: 20px 20px 0 0;
  flex-shrink: 0;

  h3 {
    margin: 0;
    font-size: 1.25rem;
    font-weight: 600;
    color: #333;
  }
}

.bottom-sheet-content {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
  // Add padding at the bottom to prevent content from being hidden under footer
  padding-bottom: 100px;
}

.bottom-sheet-footer {
  position: sticky;
  bottom: 0;
  left: 0;
  right: 0;
  background: white;
  border-top: 1px solid #e0e0e0;
  padding: 16px 20px;
  height: 76px;
  // Add safe area padding for devices with notches/home indicators
  padding-bottom: calc(16px + env(safe-area-inset-bottom));
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.08);
  flex-shrink: 0;
  z-index: 10;

  .reset-btn {
    background: none;
    border: 2px solid var(--primary-color, #fb3e52);
    color: var(--primary-color, #fb3e52);
    font-size: 16px;
    font-weight: 600;
    cursor: pointer;
    padding: 12px 24px;
    border-radius: 8px;
    transition: all 0.2s ease;
    min-height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    white-space: nowrap;

    &:active {
      background: rgba(251, 62, 82, 0.1);
      transform: scale(0.98);
    }

    @media (hover: hover) {
      &:hover {
        background: rgba(251, 62, 82, 0.1);
      }
    }
  }
}

// Transitions
.fab-scale-enter-active, .fab-scale-leave-active {
  transition: all 0.3s ease;
}

.fab-scale-enter, .fab-scale-leave-to {
  opacity: 0;
  transform: scale(0.6);
}

.bottom-sheet-enter-active, .bottom-sheet-leave-active {
  transition: all 0.3s ease;
}

.bottom-sheet-enter, .bottom-sheet-leave-to {
  opacity: 0;

  .bottom-sheet {
    transform: translateY(100%);
  }
}

@keyframes pulse {
  0% { transform: scale(1); }
  50% { transform: scale(1.1); }
  100% { transform: scale(1); }
}

// Global no-scroll class
:global(.no-scroll) {
  overflow: hidden;
  max-height: 100vh;
}
</style>
